// EDEN · mic capture for the talk-it-through session — the iOS-safe path (2026-07-01 rebuild).
//
// WHY THIS SHAPE: the old capture used getUserMedia → AudioContext → AudioWorklet (raw PCM
// streaming). On iOS Safari that pipeline is fragile — and in a home-screen PWA WebKit hands back
// EMPTY audio even with mic permission (bugs.webkit.org #185448 / #208667). So the coach heard
// silence and nothing worked. SpeechAce (which works on iPhone) records with MediaRecorder and
// uploads one clip; we do the same: MediaRecorder captures a short clip per turn, then we decode it
// OFFLINE (decodeAudioData is reliable on iOS, unlike live worklet capture) down to mono 16 kHz
// PCM16 and wrap it as a WAV — the one audio format Gemini STT accepts everywhere (audio/wav).
//
// The mic is opened per TURN and released the instant the turn ends, so the browser's red recording
// indicator never outlives a turn (honesty rail).

const TARGET_RATE = 16000;

export interface Recorder {
  /** Stop, release the mic, and return the utterance as base64 WAV (mono 16 kHz PCM16).
   *  Resolves null when nothing usable was captured (permission/standalone bug / silence). */
  stop(): Promise<{ wavB64: string } | null>;
  /** Abort without producing audio (leaving the page); releases the mic. */
  cancel(): void;
}

function pickMimeType(): string {
  // Safari/iOS → audio/mp4 (AAC); Chrome/Android → audio/webm (opus). We only need a container the
  // browser can record AND its own decodeAudioData can read back — both hold for these.
  const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg"];
  for (const m of candidates) {
    try {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* isTypeSupported can throw on odd strings — skip */
    }
  }
  return ""; // let the browser fall back to its default recorder
}

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const mimeType = pickMimeType();
  const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: BlobPart[] = [];
  rec.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  rec.start();

  const release = () => {
    try {
      stream.getTracks().forEach((tr) => tr.stop());
    } catch {
      /* already released */
    }
  };

  return {
    stop(): Promise<{ wavB64: string } | null> {
      return new Promise((resolve) => {
        rec.onstop = () => {
          release();
          void (async () => {
            try {
              const blob = new Blob(chunks, { type: rec.mimeType || mimeType || "audio/mp4" });
              if (blob.size < 512) return resolve(null); // effectively nothing captured
              const wavB64 = await blobToWavB64(blob);
              resolve(wavB64 ? { wavB64 } : null);
            } catch {
              resolve(null);
            }
          })();
        };
        try {
          if (rec.state !== "inactive") rec.stop();
          else {
            release();
            resolve(null);
          }
        } catch {
          release();
          resolve(null);
        }
      });
    },
    cancel() {
      try {
        rec.ondataavailable = null;
        rec.onstop = null;
        if (rec.state !== "inactive") rec.stop();
      } catch {
        /* ignore */
      }
      release();
    },
  };
}

// ── offline decode → mono → 16 kHz → PCM16 → WAV(base64) ────────────────────────────────────────
async function blobToWavB64(blob: Blob): Promise<string | null> {
  const AC = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext) as typeof AudioContext;
  const ctx = new AC();
  try {
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    const pcm = downmixResample(audio, TARGET_RATE);
    if (pcm.length < TARGET_RATE * 0.1) return null; // < 100 ms of real audio → treat as silence
    return wavB64FromPcm(pcm, TARGET_RATE);
  } catch {
    return null;
  } finally {
    void ctx.close().catch(() => undefined);
  }
}

function downmixResample(audio: AudioBuffer, targetRate: number): Int16Array {
  const channels = Math.max(1, audio.numberOfChannels);
  const len = audio.length;
  const chans: Float32Array[] = [];
  for (let c = 0; c < channels; c++) chans.push(audio.getChannelData(c));

  const mono = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    let sum = 0;
    for (let c = 0; c < channels; c++) sum += chans[c]![i]!;
    mono[i] = sum / channels;
  }

  const ratio = audio.sampleRate / targetRate;
  const outLen = Math.max(0, Math.floor(mono.length / ratio));
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = mono[idx] ?? 0;
    const b = mono[idx + 1] ?? a;
    const s = Math.max(-1, Math.min(1, a * (1 - frac) + b * frac));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function wavB64FromPcm(pcm: Int16Array, rate: number): string {
  const dataLen = pcm.length * 2;
  const bytes = new Uint8Array(44 + dataLen);
  const dv = new DataView(bytes.buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  dv.setUint32(4, 36 + dataLen, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  dv.setUint32(16, 16, true); // PCM fmt chunk size
  dv.setUint16(20, 1, true); // format = PCM
  dv.setUint16(22, 1, true); // mono
  dv.setUint32(24, rate, true);
  dv.setUint32(28, rate * 2, true); // byte rate
  dv.setUint16(32, 2, true); // block align
  dv.setUint16(34, 16, true); // bits per sample
  writeStr(36, "data");
  dv.setUint32(40, dataLen, true);
  bytes.set(new Uint8Array(pcm.buffer, pcm.byteOffset, dataLen), 44);

  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
